import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TextField from 'components/form-components/TextField';
import searchIcon from 'assets/images/icons/search.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import sortIcon from 'assets/images/icons/sort-arrows-grey.svg';
import FormButton from 'components/form-components/FormButton';
import NestedSelect from 'components/form-components/NestedSelect';
import TicketActions from './TicketActions';
import {
  sortOptions,
  ticketCreatedDateFilterOptions,
  ticketDueDateFilterOptions,
  ticketPriorityFilterOptions,
  ticketStatusFilterOptions,
} from '../utils';
import { CallAPI } from 'actions/General';
import DateRangeDropdown from './DateRangeDropdown';

const TicketListHeaders = ({
  filter,
  setFilter,
  handleDebouncedFilter,
  selectedRecordIds,
  selectedRows,
  actionsCallBack,
}) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState('');
  const [ticketTypeOptions, setTicketTypeOptions] = useState([]);

  const handleFilterChange = (key, value) => {
    setFilter((prevState) => ({ ...prevState, [key]: value }));
  };

  const getDisplayValue = (list, checkValue, defaultValue = 'select_select') => {
    return list.find((o) => o.value === checkValue)?.displayValue ?? defaultValue;
  };

  const fetchTicketTypeList = async () => {
    const result = await CallAPI('GET_ALL_TICKET_TYPES', {});
    if (result.status) {
      const list = [{ value: '', displayValue: 'select_all' }];
      result.data.forEach((type) => {
        list.push({ value: type._id, displayValue: type.name });
      });
      setTicketTypeOptions(list);
    }
  };

  useEffect(() => {
    fetchTicketTypeList();
  }, []);

  return (
    <div className='p-3'>
      <span className='d-block theme-font-inter-bold theme-size-1_5'>{t('text_tickets')}</span>
      <span className='d-block theme-font-inter-medium'>{t('text_tickets_header_note')}</span>
      <div className='mt-2 d-flex justify-content-between align-items-end'>
        <div className='d-flex gap-3'>
          <TextField
            value={searchText}
            name='search'
            handleChange={(name, value) => setSearchText(value)}
            placeholder='field_search'
            icon={searchIcon}
            debounceFunc={handleDebouncedFilter}
            shrink
          />

          <TicketActions
            ids={selectedRecordIds}
            selectedRows={selectedRows}
            actionsCallBack={actionsCallBack}
          />
        </div>

        <div className='d-flex flex-shrink-0 gap-3 user-select-none align-items-end'>
          <NestedSelect
            options={sortOptions}
            name='sortBy'
            handleChange={handleFilterChange}
            toggleClasses='p-2'
            toggleComponent={
              <>
                <img className='mx-2' src={sortIcon} />
                <label className='cursor-pointer'>
                  {t(getDisplayValue(sortOptions, filter.sortBy, 'text_sort_by'))}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
          />
          <NestedSelect
            displayName='text_type'
            options={ticketTypeOptions}
            name='ticketType'
            handleChange={handleFilterChange}
            shrink
            toggleClasses='p-2 border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='cursor-pointer'>
                  {t(getDisplayValue(ticketTypeOptions, filter.ticketType))}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
          />

          <div>
            <span className='d-block theme-size-0_8 mb-2'>{t('text_created_date')}</span>
            <DateRangeDropdown
              name='createdDateData'
              value={filter['createdDateData']}
              optionsList={ticketCreatedDateFilterOptions}
              handleChange={handleFilterChange}
              toggleClasses='p-2 border-grey-1-h-1'
              toggleComponent={
                <>
                  <label className='cursor-pointer'>
                    {t(
                      getDisplayValue(ticketCreatedDateFilterOptions, filter.createdDateData?.type),
                    )}
                  </label>
                  <img src={chevronGreyIcon} className='rotate-180' />
                </>
              }
            />
          </div>

          <div>
            <span className='d-block theme-size-0_8 mb-2'>{t('text_due_date')}</span>
            <DateRangeDropdown
              name='dueDateData'
              value={filter['dueDateData']}
              optionsList={ticketDueDateFilterOptions}
              handleChange={handleFilterChange}
              toggleClasses='p-2 border-grey-1-h-1'
              toggleComponent={
                <>
                  <label className='cursor-pointer'>
                    {t(getDisplayValue(ticketDueDateFilterOptions, filter.dueDateData?.type))}
                  </label>
                  <img src={chevronGreyIcon} className='rotate-180' />
                </>
              }
            />
          </div>

          <NestedSelect
            displayName='text_status'
            options={ticketStatusFilterOptions}
            name='status'
            handleChange={handleFilterChange}
            shrink
            toggleClasses='p-2 border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='cursor-pointer'>
                  {t(getDisplayValue(ticketStatusFilterOptions, filter.status))}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
          />
          <NestedSelect
            displayName='text_priority'
            options={ticketPriorityFilterOptions}
            name='priority'
            handleChange={handleFilterChange}
            shrink
            toggleClasses='p-2 border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='cursor-pointer'>
                  {t(getDisplayValue(ticketPriorityFilterOptions, filter.priority))}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
          />
          <FormButton
            text={'Unassigned'}
            variant={filter.unassigned ? 'grey-1' : 'white-1'}
            onClick={() => handleFilterChange('unassigned', !filter.unassigned)}
          />
          <FormButton
            text={'Overdue tickets'}
            variant={filter.overdue ? 'red-1' : 'red-4'}
            onClick={() => handleFilterChange('overdue', !filter.overdue)}
          />
        </div>
      </div>
    </div>
  );
};

TicketListHeaders.propTypes = {
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  handleDebouncedFilter: PropTypes.func.isRequired,
  selectedRecordIds: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  actionsCallBack: PropTypes.func,
};

export default TicketListHeaders;
